// The UI never enables automation optimistically. Stripe setup completion is
// the server's authority. Scope every async result to its original workspace.
export const autoTopupData = (root) => root?._autoTopup?.data || root?._autoTopup
export const autoTopupWorkspace = (root) =>
  root?.workspace?.id || root?.workspace?._id || root?.jwtClaims?.active_workspace_id
const ratesFor = (root) => root?._usageRates?.data || root?._usageRates || {}
const dollarsToCents = (value) => {
  if (!/^\d+(\.\d{1,2})?$/.test(String(value))) return NaN
  return Math.round(Number(value) * 100)
}

export const autoTopupRule = (root) => {
  const data = autoTopupData(root) || {}
  const draft = root?._autoTopupDraft
  const rates = ratesFor(root)
  const amountCents = draft ? dollarsToCents(draft.amountUsd) : data.amountCents
  return {
    amountCents,
    thresholdCredits: draft ? Number(draft.thresholdCredits) : data.thresholdCredits,
    monthlyLimitCents: draft ? dollarsToCents(draft.limitUsd) : data.monthlyLimitCents,
    credits: draft
      ? rates.centsPerCredit > 0
        ? Math.floor(amountCents / rates.centsPerCredit)
        : null
      : data.credits
  }
}

export const autoTopupValidation = (root) => {
  const rates = ratesFor(root)
  const rule = autoTopupRule(root)
  if (
    !(rates.centsPerCredit > 0) ||
    !(rates.topupMinCents > 0) ||
    !(rates.topupMaxCents >= rates.topupMinCents)
  )
    return 'rates'
  if (
    !Number.isSafeInteger(rule.amountCents) ||
    rule.amountCents < rates.topupMinCents ||
    rule.amountCents > rates.topupMaxCents
  )
    return 'amount'
  if (
    !Number.isSafeInteger(rule.thresholdCredits) ||
    rule.thresholdCredits < 1 ||
    rule.thresholdCredits >= rule.credits
  )
    return 'threshold'
  if (
    !Number.isSafeInteger(rule.monthlyLimitCents) ||
    rule.monthlyLimitCents < rule.amountCents ||
    rule.monthlyLimitCents > rates.topupMaxCents
  )
    return 'limit'
  return null
}

export const autoTopupMoney = (cents) =>
  Number.isFinite(cents)
    ? new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 2
      }).format(cents / 100)
    : '—'
export const autoTopupNumber = (value) =>
  Number.isFinite(value) ? value.toLocaleString('en-US') : '—'

export const autoTopupEdit = function (root) {
  const data = autoTopupData(root) || {}
  const rates = ratesFor(root)
  const amount = data.amountCents || rates.topupMinCents
  const credits = rates.centsPerCredit > 0 ? Math.floor(amount / rates.centsPerCredit) : null
  root.update({
    _autoTopupDraft: {
      thresholdCredits:
        data.thresholdCredits ?? (credits ? Math.max(1, Math.floor(credits / 5)) : ''),
      amountUsd: amount ? String(amount / 100) : '',
      limitUsd: data.monthlyLimitCents
        ? String(data.monthlyLimitCents / 100)
        : amount
          ? String(amount / 100)
          : ''
    },
    _autoTopupStep: 'edit',
    _autoTopupConsent: false,
    _autoTopupError: null,
    _autoTopupTouched: false,
    _autoTopupCheckoutUrl: null
  })
}

export const autoTopupChange = function (root, field, value) {
  root.update({
    _autoTopupDraft: { ...root._autoTopupDraft, [field]: value },
    _autoTopupConsent: false,
    _autoTopupError: null
  })
}

export const autoTopupReview = function (root) {
  root.update({ _autoTopupTouched: true })
  if (!autoTopupValidation(root))
    root.update({ _autoTopupStep: 'review', _autoTopupConsent: false })
}

export const autoTopupClose = function (root) {
  root.update({
    _autoTopupDraft: null,
    _autoTopupStep: null,
    _autoTopupConsent: false,
    _autoTopupError: null,
    _autoTopupCheckoutUrl: null
  })
}

export const autoTopupAction = async function (root, action) {
  if (root._autoTopupBusy) return
  const workspaceId = autoTopupWorkspace(root)
  if (!workspaceId) return
  const request = (root._autoTopupRequest || 0) + 1
  const sdk = this.call('getSDK')
  const method = {
    setup: 'setupAutoTopup',
    pause: 'pauseAutoTopup',
    refresh: 'refreshAutoTopup',
    load: 'getAutoTopup'
  }[action]
  if (!method || typeof sdk?.[method] !== 'function') {
    root.update({ _autoTopupError: 'unavailable' })
    return
  }
  if (
    action === 'setup' &&
    (autoTopupValidation(root) || !root._autoTopupConsent || root._autoTopupStep !== 'review')
  )
    return
  root.update({ _autoTopupBusy: true, _autoTopupError: null, _autoTopupRequest: request })
  const current = () =>
    autoTopupWorkspace(root) === workspaceId && root._autoTopupRequest === request
  try {
    const payload =
      action === 'setup'
        ? { ...autoTopupRule(root), consent: true, returnUrl: globalThis.document?.URL }
        : undefined
    const response = await sdk[method](workspaceId, payload)
    const data = response?.data || response
    if (!current()) return
    if (action === 'setup') {
      // Native external link, never feed a Stripe URL into the app router.
      const url = new URL(data?.url)
      if (url.protocol !== 'https:' || url.hostname !== 'checkout.stripe.com')
        throw new Error('invalid_checkout_url')
      root.update({
        _autoTopupCheckoutUrl: url.toString(),
        _autoTopupStep: 'card',
        _autoTopup: {
          ...autoTopupData(root),
          ...autoTopupRule(root),
          enabled: false,
          status: 'setup_required'
        }
      })
    } else {
      root.update({ _autoTopup: data })
      if (action === 'pause') autoTopupClose(root)
    }
  } catch (error) {
    if (current())
      root.update({
        _autoTopupError:
          error?.cause?.error === 'forbidden'
            ? 'forbidden'
            : error?.cause?.error === 'payment_in_progress'
              ? 'inProgress'
              : 'failed'
      })
  } finally {
    if (current()) root.update({ _autoTopupBusy: false })
  }
}
